/** Save-as-template prompt — Tier 3 Group D Slice 1c.
 *
 *  Tiny single-purpose dialog: asks for a name, shows the captured
 *  dimensions + DPI (read-only), and writes a `UserTemplate` row.
 *  The dialog doesn't read the editor state itself — the parent
 *  passes `width`, `height`, `dpi` so the snapshot is taken at the
 *  moment File → Save as Template is invoked. */

import { useEffect, useState } from 'react';
import { LayoutTemplate, X } from 'lucide-react';
import { useTemplates } from '../lib/use-templates';
import { USER_TEMPLATES_LIMITS } from '../lib/user-templates-prefs';
import { toast } from '@/components/ui/use-toast';

interface Props {
  onClose: () => void;
  /** Width / height / optional DPI captured by the caller at the
   *  moment the user opened this dialog. */
  width: number;
  height: number;
  dpi?: number;
  /** Suggested initial name — typically derived from the current
   *  document's file name minus extension. */
  defaultName?: string;
}

export function SaveTemplateDialog({ onClose, width, height, dpi, defaultName }: Props) {
  const { saveTemplate, userTemplates } = useTemplates();
  const [name, setName] = useState(defaultName ?? 'My template');

  // Close on Escape — matches the rest of the editor's modals.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const atCap = userTemplates.length >= USER_TEMPLATES_LIMITS.maxTemplates;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (atCap) return;
    const trimmed = name.trim() || 'Untitled template';
    saveTemplate({ name: trimmed, width, height, dpi });
    toast({
      title: 'Template saved',
      description: `"${trimmed}" added to My templates.`,
    });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
      role="presentation"
    >
      <form
        role="dialog"
        aria-label="Save as template"
        aria-modal="true"
        className="bg-background border border-border rounded-lg shadow-xl w-[92vw] max-w-md flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <LayoutTemplate className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-base font-semibold flex-1">Save as Template</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-2 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-4 py-3 space-y-3">
          <label className="block">
            <span className="text-xs font-medium text-muted-foreground uppercase">Name</span>
            <input
              type="text"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              className="w-full mt-1 text-sm bg-background border border-border rounded-md px-2 py-1.5 focus:border-primary focus:outline-none"
              aria-label="Template name"
            />
          </label>

          <div className="text-xs text-muted-foreground space-y-0.5">
            <div className="flex items-center justify-between">
              <span>Canvas size</span>
              <span className="tabular-nums">
                {width} × {height} px
              </span>
            </div>
            {dpi ? (
              <div className="flex items-center justify-between">
                <span>DPI</span>
                <span className="tabular-nums">{dpi}</span>
              </div>
            ) : null}
            <p className="pt-2 text-[11px] italic">
              V1 templates store size + DPI only. Layer content stays with the source document.
            </p>
          </div>

          {atCap && (
            <p className="text-xs text-destructive">
              You have {USER_TEMPLATES_LIMITS.maxTemplates} saved templates — delete one before
              saving another.
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded-md hover:bg-accent text-muted-foreground"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={atCap}
            className="text-sm px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            Save template
          </button>
        </div>
      </form>
    </div>
  );
}
